import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { WalletDebit } from "../types/walletDebits.types";
import { describeDebitSend } from "../utils/walletDebit";
import { WalletDebitManage } from "./WalletDebitManage";
import { WalletDebitStatusPill } from "./WalletDebitStatusPill";

export interface WalletDebitDetailDrawerProps {
  debit: WalletDebit | null;
  onClose: () => void;
  onUpdated: (debit: WalletDebit) => void;
}

/** One debit from the history: what was recorded, what is left to do with it, its proof and its timeline. */
export function WalletDebitDetailDrawer({ debit, onClose, onUpdated }: WalletDebitDetailDrawerProps) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  return (
    <ColocaDrawer
      open={!!debit}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="md"
      title={debit ? debit.reference : t("done_receipt_label")}
      description={debit ? formatDateTime(debit.created_at, { locale: activeLanguage }) : undefined}
      pinLastChild={false}
    >
      {debit && (
        <div className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{t("done_amount")}</p>
              <div className="mt-1 text-foreground">
                <ColocaAmountDisplay
                  value={`-${debit.amount}`}
                  currency={debit.currency}
                  fractionDigits={minorUnitsFor(debit.currency)}
                  size="xl"
                  signed
                />
              </div>
            </div>
            <WalletDebitStatusPill debit={debit} />
          </div>
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Field label={t("done_organization")} value={debit.organization_name ?? "—"} />
            <Field label={t("done_wallet")} value={t("review_wallet_value", { currency: debit.currency })} />
            <SendFields debit={debit} locale={activeLanguage} />
            <Field label={t("done_concept")} value={debit.concept} />
            <Field label={t("review_note")} value={debit.internal_note ?? "—"} />
            <Field label={t("col_by")} value={debit.created_by_email ?? "—"} />
            {debit.sent_by_email && <Field label={t("done_sent_by")} value={debit.sent_by_email} />}
          </dl>
          <WalletDebitManage debit={debit} canEdit onChange={onUpdated} />
        </div>
      )}
    </ColocaDrawer>
  );
}

/** The corridor the send goes through, what is (or was) sent by hand and the fees the organization pays. */
export function SendFields({ debit, locale }: { debit: WalletDebit; locale: string }) {
  const { t } = useTranslations("walletDebits");
  const send = describeDebitSend(debit, locale);
  return (
    <>
      <Field label={t("review_corridor")} value={send.rail ?? t("corridor_other")} />
      <Field
        label={debit.status === "RESERVED" ? t("review_to_send_by_hand") : t("review_send_by_hand")}
        value={send.sent}
      />
      {send.commission && send.taxes && (
        <Field
          label={t("done_fees")}
          value={t("send_preview_fees", { commission: send.commission, taxes: send.taxes })}
        />
      )}
    </>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("mt-0.5 break-words font-medium text-foreground", mono && "font-mono text-[13px]")}>
        {value}
      </dd>
    </div>
  );
}
